// src/pages/courier/BranchDistrictModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, MapPin, Search, CheckSquare, Square, Save, AlertCircle } from 'lucide-react';
import stateDistrictData from '../StatenDistrict.json';

const BranchDistrictModal = ({
  isOpen,
  onClose,
  branch,
  courier,
  currentAssignedServiceAreas = [], // All service areas for this branch
  onSaveAssignments
}) => {
  const statesList = useMemo(() => stateDistrictData?.states || [], []);

  // Default to branch's state or Odisha or first state
  const defaultStateName = branch?.address?.stateName || 'Odisha';
  const [selectedState, setSelectedState] = useState(defaultStateName);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistricts, setSelectedDistricts] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Identify all districts assigned to this branch across ALL states
  const branchAssignedAreas = useMemo(() => {
    return currentAssignedServiceAreas.filter((sa) => sa.branchId === branch?.branchId);
  }, [currentAssignedServiceAreas, branch]);

  // District list for current state
  const currentDistricts = useMemo(() => {
    const found = statesList.find((s) => s.state.toLowerCase() === selectedState?.toLowerCase());
    return found ? found.districts : [];
  }, [statesList, selectedState]);

  // Set of district keys assigned in the CURRENTLY selected state
  const assignedInCurrentState = useMemo(() => {
    const current = new Set();
    const selStateNorm = (selectedState || '').trim().toLowerCase();

    branchAssignedAreas.forEach((sa) => {
      const saStateNorm = (sa.stateName || sa.stateCode || '').trim().toLowerCase();
      if (
        saStateNorm === selStateNorm ||
        saStateNorm.replace(/\s+/g, '_') === selStateNorm.replace(/\s+/g, '_')
      ) {
        const rawDist = (sa.districtName || sa.districtCode || '').trim();
        // Match against currentDistricts with case-insensitivity
        const matched = currentDistricts.find(
          (d) =>
            d.toLowerCase() === rawDist.toLowerCase() ||
            d.replace(/\s+/g, '_').toLowerCase() === rawDist.toLowerCase()
        );
        current.add(matched || rawDist);
      }
    });
    return current;
  }, [branchAssignedAreas, selectedState, currentDistricts]);

  // Reset selected districts whenever state changes or modal opens
  useEffect(() => {
    if (isOpen && branch) {
      const initialSelected = new Set(assignedInCurrentState);
      setSelectedDistricts(initialSelected);
    }
  }, [isOpen, branch, selectedState, assignedInCurrentState]);

  // Filtered districts matching search query
  const filteredDistricts = useMemo(() => {
    if (!searchQuery.trim()) return currentDistricts;
    const q = searchQuery.toLowerCase();
    return currentDistricts.filter((d) => d.toLowerCase().includes(q));
  }, [currentDistricts, searchQuery]);

  if (!isOpen || !branch) return null;

  const handleToggleDistrict = (distName) => {
    const next = new Set(selectedDistricts);
    if (next.has(distName)) {
      next.delete(distName);
    } else {
      next.add(distName);
    }
    setSelectedDistricts(next);
  };

  const handleSelectAllFiltered = () => {
    const next = new Set(selectedDistricts);
    filteredDistricts.forEach((d) => next.add(d));
    setSelectedDistricts(next);
  };

  const handleClearFiltered = () => {
    const next = new Set(selectedDistricts);
    filteredDistricts.forEach((d) => next.delete(d));
    setSelectedDistricts(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Find additions and removals compared to assignedInCurrentState
      const toAssign = [];
      const toRemove = [];

      // Newly checked
      selectedDistricts.forEach((d) => {
        if (!assignedInCurrentState.has(d)) {
          toAssign.push({ districtCode: d, districtName: d });
        }
      });

      // Newly unchecked
      assignedInCurrentState.forEach((d) => {
        if (!selectedDistricts.has(d)) {
          toRemove.push(d);
        }
      });

      await onSaveAssignments({
        courierId: branch.courierId,
        branchId: branch.branchId,
        stateName: selectedState,
        stateCode: selectedState,
        toAssign,
        toRemove
      });

      onClose();
    } catch (err) {
      console.error('Error saving district assignments:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-modal-overlay">
      <div className="cm-modal-container cm-modal-lg">
        <div className="cm-modal-header">
          <div className="cm-modal-title-group">
            <div className="cm-modal-icon-badge">
              <MapPin size={22} color="#8a6b32" />
            </div>
            <div>
              <h3 className="cm-modal-title">Service Districts Coverage</h3>
              <p className="cm-modal-subtitle">
                Coverage assignment for <strong>{branch.branchName}</strong> ({branch.branchCode}) &bull;{' '}
                Branch Base District:{' '}
                <strong>
                  {branch.address?.districtName || branch.address?.districtCode || branch.address?.city || '—'}
                </strong>{' '}
                ({branch.address?.stateName || branch.address?.stateCode || 'Odisha'})
              </p>
            </div>
          </div>
          <button className="cm-btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cm-modal-body">
          {/* Branch summary & totals banner */}
          <div className="cm-summary-banner">
            <div className="cm-summary-banner-item">
              <span className="cm-summary-banner-label">Total Assigned (All States)</span>
              <span className="cm-summary-banner-val">{branchAssignedAreas.length}</span>
            </div>
            <div className="cm-summary-banner-item">
              <span className="cm-summary-banner-label">Assigned in {selectedState}</span>
              <span className="cm-summary-banner-val cm-val-accent">
                {selectedDistricts.size} / {currentDistricts.length}
              </span>
            </div>
            <div className="cm-summary-banner-item">
              <span className="cm-summary-banner-label">Branch Status</span>
              <span
                className={`cm-badge ${
                  branch.active !== false ? 'cm-badge-success' : 'cm-badge-inactive'
                }`}
              >
                {branch.active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* State selection & search bar */}
          <div className="cm-district-filter-row">
            <div className="cm-state-picker">
              <label className="cm-label">Select State</label>
              <select
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  setSearchQuery('');
                }}
                className="cm-select"
              >
                {statesList.map((st) => (
                  <option key={st.state} value={st.state}>
                    {st.state} ({st.districts.length} Districts)
                  </option>
                ))}
              </select>
            </div>

            <div className="cm-district-search">
              <label className="cm-label">Search District</label>
              <div className="cm-input-wrapper">
                <Search size={16} className="cm-input-icon" />
                <input
                  type="text"
                  placeholder="Filter districts in current state..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="cm-input"
                />
              </div>
            </div>
          </div>

          {/* Bulk actions bar */}
          <div className="cm-district-actions-bar">
            <div className="cm-district-counter">
              Showing <strong>{filteredDistricts.length}</strong> of {currentDistricts.length} districts &bull;{' '}
              <span className="cm-text-gold">{selectedDistricts.size} Selected</span>
            </div>
            <div className="cm-district-quick-btns">
              <button
                type="button"
                className="cm-btn-secondary cm-btn-xs"
                onClick={handleSelectAllFiltered}
              >
                <CheckSquare size={13} /> Select All
              </button>
              <button
                type="button"
                className="cm-btn-secondary cm-btn-xs"
                onClick={handleClearFiltered}
              >
                <Square size={13} /> Clear
              </button>
            </div>
          </div>

          {/* District checkboxes grid */}
          <div className="cm-district-grid">
            {filteredDistricts.length === 0 ? (
              <div className="cm-no-districts">
                <AlertCircle size={20} color="#94a3b8" />
                <span>No districts found matching "{searchQuery}"</span>
              </div>
            ) : (
              filteredDistricts.map((dist) => {
                const isChecked = selectedDistricts.has(dist);
                const wasAssigned = assignedInCurrentState.has(dist);
                return (
                  <label
                    key={dist}
                    className={`cm-district-card ${isChecked ? 'cm-district-card-checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleDistrict(dist)}
                    />
                    <div className="cm-district-info">
                      <span className="cm-district-name">{dist}</span>
                      {wasAssigned && (
                        <span className="cm-district-tag">Currently Active</span>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="cm-modal-footer">
          <button type="button" className="cm-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="cm-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'Saving Assignments...' : 'Save District Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchDistrictModal;
